/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { LoadingController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, isEqual } from 'lodash-es';

import { select, Store } from '@ngrx/store';
import { noop, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as AccountActions from '@account/account.actions';
import * as fromAccount from '@account/account.reducers';

import { Address, CustomerPersonalDetails } from '@shared/models/Account';
import { State } from '@shared/models/State';
import { ToastService } from '@shared/services/toast.service';
import { TrackingService } from '@shared/services/tracking.service';

const POSTCODE_PATTERNS: Record<string, RegExp> = {
  CH: /^\d{4}$/,
  DE: /^\d{5}$/
};

@Component({
  selector: 'app-account-address-modal',
  styleUrls: ['./account-address-modal.component.scss'],
  templateUrl: './account-address-modal.component.html',
  standalone: false
})
export class AccountAddressModalComponent {
  address: Address;
  addressForm: FormGroup;
  protected readonly onDestroy$ = new Subject<void>();

  constructor(
    private modalController: ModalController,
    private fb: FormBuilder,
    private store: Store<State>,
    private toastService: ToastService,
    private loadingController: LoadingController,
    private translateService: TranslateService,
    private trackingService: TrackingService
  ) {
    this.store.pipe(select(fromAccount.selectAccountDetails), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.address = value;
    });
    this.addressForm = this.fb.group({
      city: ['', [Validators.required]],
      country: [{ value: '', disabled: true }],
      number: ['', [Validators.required]],
      street: ['', [Validators.required]],
      zip: ['', [Validators.required, this.postcodeValidator()]]
    });
  }

  ionViewWillEnter(): void {
    const address = cloneDeep(this.address);
    if (address && address.id) {
      delete address.id;
    }
    this.addressForm.setValue(address);
    this.addressForm.get('zip')?.updateValueAndValidity();
  }

  dismiss(): void {
    this.modalController
      .dismiss({
        dismissed: true
      })
      .finally(noop);
  }

  hasError(control: string, error: string): boolean {
    if (!this.addressForm.get(control)) {
      return false;
    }
    return this.addressForm.get(control).hasError(error) && this.addressForm.get(control).touched;
  }

  // eslint-disable-next-line consistent-return
  async save() {
    if (this.addressForm.invalid) {
      return this.addressForm.markAllAsTouched();
    }
    this.trackingService.trackActivity('[Button] Update address');
    if (isEqual(this.addressForm.getRawValue(), this.address)) {
      this.dismiss();
      await this.toastService.show(this.translateService.instant('ACCOUNT.personal.flash.success'));
    } else {
      const loading = await this.loadingController.create({});
      await loading.present();
      const personalDetails: Partial<CustomerPersonalDetails> = {
        street: this.addressForm.get('street').value,
        number: this.addressForm.get('number').value,
        zip: this.addressForm.get('zip').value,
        city: this.addressForm.get('city').value
      };
      this.store.dispatch(AccountActions.updateAddress({ personalDetails }));
    }
  }

  private postcodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value ?? '');
      if (!value) {
        return null;
      }

      const country = this.getAddressCountry(control);
      const pattern = country ? POSTCODE_PATTERNS[country] : null;

      return pattern && !pattern.test(value) ? { invalidZip: true } : null;
    };
  }

  private getAddressCountry(control: AbstractControl): string {
    const country = control.parent?.getRawValue()?.country ?? this.address?.country;

    return String(country ?? '').toUpperCase();
  }
}
